import { cn } from "@repo/design-system/utils";
import { Loader2, Mic, Square } from "lucide-react";

import type { RecorderStatus } from "@/hooks/useAudioRecorder";
import { VOICE_ORDER_MAX_DURATION_MS } from "@/lib/voice-order";

/**
 * The copy each recorder flow supplies for the idle/requesting/recording
 * states of {@link VoiceRecorderChrome}. Kept out of the shared component so
 * each flow (order vs. message) owns its own wording - only the visual
 * scaffold (ring, button, layout) is shared, per KAN-192.
 */
export interface VoiceRecorderChromeCopy {
  idleTitle: string;
  idleDescription: string;
  requestingTitle: string;
  requestingDescription: string;
  recordingTitle: string;
  recordingHint: string;
}

export interface VoiceRecorderChromeProps {
  status: RecorderStatus;
  remainingMs: number;
  /** Defaults to the shared 60s voice-capture cap (KAN-143/KAN-192). */
  maxDurationMs?: number;
  onStart: () => void;
  onStop: () => void;
  copy: VoiceRecorderChromeCopy;
}

/** Format a millisecond countdown as `m:ss`. */
export function formatCountdown(ms: number): string {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/** SVG ring that depletes as the recording window runs down. */
export function CountdownRing({ fraction }: { fraction: number }) {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - Math.min(1, Math.max(0, fraction)));
  return (
    <svg
      className="absolute inset-0 h-full w-full -rotate-90"
      viewBox="0 0 120 120"
      aria-hidden="true"
    >
      <circle
        cx="60"
        cy="60"
        r={radius}
        fill="none"
        strokeWidth="4"
        className="stroke-gray-200 dark:stroke-neutral-700"
      />
      <circle
        cx="60"
        cy="60"
        r={radius}
        fill="none"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className="stroke-purple-600 transition-[stroke-dashoffset] duration-100 ease-linear dark:stroke-purple-400"
      />
    </svg>
  );
}

/**
 * The visual chrome shared by every voice-capture flow (order, message):
 * the idle mic button + prompt, the "requesting permission" state, and the
 * live recording view (pulsing mic, depleting countdown ring, stop button).
 * Excludes the failure card, which is its own shared component,
 * `VoiceRecorderFailureCard` - each flow still supplies its own `ERROR_COPY`
 * map and fallback action (e.g. `VoiceRecorderPanel`'s catalog search vs.
 * `VoiceMessageRecorderPanel`'s cancel), so callers render that separately
 * and fall through to this component only for the idle/requesting/recording
 * states (KAN-192).
 */
export function VoiceRecorderChrome({
  status,
  remainingMs,
  maxDurationMs = VOICE_ORDER_MAX_DURATION_MS,
  onStart,
  onStop,
  copy
}: VoiceRecorderChromeProps) {
  if (status === "recording") {
    const fraction = remainingMs / maxDurationMs;
    return (
      <div className="flex flex-col items-center gap-5 px-6 py-8 text-center">
        <div className="relative flex h-32 w-32 items-center justify-center">
          <CountdownRing fraction={fraction} />
          <span className="absolute inline-flex h-24 w-24 animate-ping rounded-full bg-purple-400/30" />
          <button
            type="button"
            onClick={onStop}
            aria-label="Stop recording"
            className="relative flex h-20 w-20 items-center justify-center rounded-full bg-purple-600 text-white shadow-lg transition-colors hover:bg-purple-700"
          >
            <Square className="h-7 w-7 fill-current" aria-hidden="true" />
          </button>
        </div>
        <div className="space-y-1">
          <p className="text-base font-semibold text-gray-900 dark:text-white">
            {copy.recordingTitle}
          </p>
          <p
            className="text-2xl font-semibold tabular-nums text-purple-600 dark:text-purple-400"
            aria-live="polite"
          >
            {formatCountdown(remainingMs)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {copy.recordingHint}
          </p>
        </div>
      </div>
    );
  }

  const requesting = status === "requesting-permission";
  return (
    <div className="flex flex-col items-center gap-5 px-6 py-8 text-center">
      <button
        type="button"
        onClick={onStart}
        disabled={requesting}
        aria-label="Start recording"
        className={cn(
          "flex h-24 w-24 items-center justify-center rounded-full bg-purple-600 text-white shadow-lg transition-all hover:scale-105 hover:bg-purple-700 disabled:opacity-70",
          !requesting && "hover:shadow-xl"
        )}
      >
        {requesting ? (
          <Loader2 className="h-9 w-9 animate-spin" aria-hidden="true" />
        ) : (
          <Mic className="h-9 w-9" aria-hidden="true" />
        )}
      </button>
      <div className="space-y-1">
        <p className="text-base font-semibold text-gray-900 dark:text-white">
          {requesting ? copy.requestingTitle : copy.idleTitle}
        </p>
        <p className="mx-auto max-w-xs text-sm text-gray-500 dark:text-gray-400">
          {requesting ? copy.requestingDescription : copy.idleDescription}
        </p>
      </div>
    </div>
  );
}
